import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { WorkDateField } from "@api/workItem.api";
import type { ClientsWorkOverviewRow } from "../clientsWorkOverviewViewConfig";
import { WorkDateControl } from "../editing/WorkDateControl";
import { useWorkEditability } from "../editing/workItemEditing";
import { formatWorkDate } from "./WorkCloseDateCell";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";
import styles from "./WorkEditableDateCell.module.scss";

/**
 * Builds a List cell for a work date: where the user can edit the work, it
 * opens a calendar to update it; otherwise it shows the date as plain text.
 */
export const makeEditableDateCell = (field: WorkDateField) =>
    memo(
        ({
            row,
        }: CellContext<
            ViewTableFeatures,
            ClientsWorkOverviewRow,
            string | null
        >) => {
            const { isItemEditable } = useWorkEditability();
            const work = row.original;
            if (work.rowType !== "work") {
                return EMPTY_CELL_VALUE;
            }
            const date = work[field];
            const value = date ? (
                <span className={styles.value}>{formatWorkDate(date)}</span>
            ) : null;
            if (!isItemEditable(work)) {
                return value ?? EMPTY_CELL_VALUE;
            }
            return (
                <ViewTableCell>
                    <div>
                        <CaptureClicks>
                            <WorkDateControl
                                row={work}
                                field={field}
                                trigger={
                                    <EditableCellContent
                                        isEmpty={!date}
                                        addText="Add date"
                                        hideIcon
                                    >
                                        {value}
                                    </EditableCellContent>
                                }
                            />
                        </CaptureClicks>
                    </div>
                </ViewTableCell>
            );
        },
    );
